import { useCallback, useEffect, useState } from 'react'

/**
 * `useState` that remembers itself in `localStorage`.
 *
 * Used for choices a walk-around operator should not have to re-make after an
 * accidental reload — which capture mode is running, and whose phone is doing it.
 * Every access is guarded: private windows and blocked site data both throw.
 */
export function usePersistentState<T>(
  key: string,
  initial: T,
  isValid: (value: unknown) => value is T,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      if (raw !== null) {
        const parsed: unknown = JSON.parse(raw)
        if (isValid(parsed)) return parsed
      }
    } catch {
      // Fall through to the default.
    }
    return initial
  })

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Not remembering is survivable; throwing here is not.
    }
  }, [key, value])

  return [value, useCallback((next: T) => setValue(next), [])]
}
